import React, { useState, useEffect, useMemo } from 'react'
import { useAppSelector, useAppDispatch } from '../redux/hooks'
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  resetAdminPassword,
  suspendAdminUser,
  unsuspendAdminUser,
  setFilters,
  clearError,
  AdminUserListItem,
} from '../redux/reducers/adminUsersSlice'
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Key,
  Shield,
  Clock,
  UserCheck,
  UserX,
  UserCog,
  Eye,
  Download,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import AdminModal from '../components/admin/AdminModal'
import AdminDetailsModal from '../components/admin/AdminDetailsModal'
import PasswordResetModal from '../components/admin/PasswordResetModal'
import AuditLogModal from '../components/admin/AuditLogModal'
import { formatDistanceToNow } from 'date-fns'

interface Admin extends AdminUserListItem {
  firstName?: string
  lastName?: string
  status: 'active' | 'inactive' | 'suspended'
  lastLogin: Date | null
  department?: string
  phoneNumber?: string
  twoFactorEnabled?: boolean
  loginAttempts?: number
}

const AdminManagement: React.FC = () => {
  const dispatch = useAppDispatch()
  const { admins: adminList, loading, error, pagination, filters } = useAppSelector((state) => state.adminUsers)
  const { user: currentUser } = useAppSelector((state) => state.auth)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedRole, setSelectedRole] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showAuditLogModal, setShowAuditLogModal] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [showContextMenu, setShowContextMenu] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState('')
  
  // Map API data to component format - use useMemo to prevent recalculation
  const admins: Admin[] = useMemo(() => adminList.map(admin => ({
    ...admin,
    firstName: admin.name.split(' ')[0] || '',
    lastName: admin.name.split(' ').slice(1).join(' ') || '',
    status: admin.isSuspended ? 'suspended' : 'active',
    lastLogin: admin.lastLoginAt ? new Date(admin.lastLoginAt) : null,
    loginAttempts: 0,
    twoFactorEnabled: false,
  })), [adminList])

  // Initial fetch on mount only
  useEffect(() => {
    dispatch(fetchAdminUsers({
      page: 1,
      limit: 20,
      search: '',
      role: 'all',
      status: 'all',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }))
  }, [dispatch])
  
  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])
  
  const showSuccess = (message: string) => {
    setSuccessMessage(message)
  }
  
  // Manual refresh function
  const handleRefresh = () => {
    dispatch(fetchAdminUsers({
      page: 1,
      limit: 20,
      search: searchQuery,
      role: selectedRole === 'all' ? undefined : selectedRole,
      status: selectedStatus === 'all' ? 'all' : selectedStatus as 'active' | 'suspended' | 'all',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }))
  }

  // Filter admins locally based on current data
  const filteredAdmins = useMemo(() => {
    let filtered = [...admins]

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (admin) =>
          admin.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          admin.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          admin.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Role filter
    if (selectedRole !== 'all') {
      filtered = filtered.filter((admin) => admin.role === selectedRole)
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter((admin) => admin.status === selectedStatus)
    }

    return filtered
  }, [admins, searchQuery, selectedRole, selectedStatus])

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-500/10 text-purple-500'
      case 'support_admin':
        return 'bg-blue-500/10 text-blue-500'
      case 'content_admin':
        return 'bg-green-500/10 text-green-500'
      case 'finance_admin':
        return 'bg-orange-500/10 text-orange-500'
      default:
        return 'bg-gray-500/10 text-gray-500'
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500'
      case 'inactive':
        return 'bg-gray-500/10 text-gray-500'
      case 'suspended':
        return 'bg-red-500/10 text-red-500'
      default:
        return 'bg-gray-500/10 text-gray-500'
    }
  }

  const handleEditAdmin = (admin: Admin) => {
    setSelectedAdmin(admin)
    setShowEditModal(true)
    setShowContextMenu(null)
  }
  
  const handleCreateAdmin = async (data: any) => {
    try {
      await dispatch(createAdminUser({
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        role: data.role,
        sendWelcomeEmail: true,
      })).unwrap()
      setShowCreateModal(false)
      showSuccess('Admin user created successfully')
      // Refresh the list
      handleRefresh()
    } catch (error: any) {
      console.error('Failed to create admin:', error)
      throw error // Re-throw to let modal handle it
    }
  }
  
  const handleUpdateAdmin = async (data: any) => {
    if (!selectedAdmin) return
    try {
      await dispatch(updateAdminUser({
        adminId: selectedAdmin.id,
        data: {
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          role: data.role,
        },
      })).unwrap()
      setShowEditModal(false)
      setSelectedAdmin(null)
      showSuccess('Admin user updated successfully')
    } catch (error: any) {
      console.error('Failed to update admin:', error)
      throw error
    }
  }

  const handleViewDetails = (admin: Admin) => {
    setSelectedAdmin(admin)
    setShowDetailsModal(true)
    setShowContextMenu(null)
  }
  
  const handleViewAuditLogs = (admin: Admin) => {
    setSelectedAdmin(admin)
    setShowAuditLogModal(true)
    setShowContextMenu(null)
  }

  const handleResetPassword = (admin: Admin) => {
    setSelectedAdmin(admin)
    setShowPasswordModal(true)
    setShowContextMenu(null)
  }
  
  const handlePasswordReset = async (password: string) => {
    if (!selectedAdmin) return
    try {
      await dispatch(resetAdminPassword({
        adminId: selectedAdmin.id,
        data: {
          temporaryPassword: password,
          sendEmail: true,
          forceChangeOnLogin: true,
        },
      })).unwrap()
      setShowPasswordModal(false)
      setSelectedAdmin(null)
      showSuccess('Password reset successfully')
    } catch (error) {
      console.error('Failed to reset password:', error)
    }
  }

  const handleDeleteAdmin = async (admin: Admin) => {
    // Prevent self-deletion
    if (currentUser?.email === admin.email) {
      alert('You cannot delete your own account')
      setShowContextMenu(null)
      return
    }
    
    if (confirm(`Are you sure you want to delete ${admin.firstName} ${admin.lastName}?`)) {
      try {
        await dispatch(deleteAdminUser(admin.id)).unwrap()
        showSuccess('Admin user deleted successfully')
      } catch (error) {
        console.error('Failed to delete admin:', error)
      }
    }
    setShowContextMenu(null)
  }

  const handleToggleStatus = async (admin: Admin) => {
    // Prevent self-suspension
    if (currentUser?.email === admin.email) {
      alert('You cannot suspend your own account')
      setShowContextMenu(null)
      return
    }
    
    try {
      if (admin.isSuspended) {
        await dispatch(unsuspendAdminUser(admin.id)).unwrap()
        showSuccess('Admin user activated successfully')
      } else {
        await dispatch(suspendAdminUser(admin.id)).unwrap()
        showSuccess('Admin user suspended successfully')
      }
    } catch (error) {
      console.error('Failed to toggle admin status:', error)
    }
    setShowContextMenu(null)
  }

  // Check if current user is SuperAdmin
  console.log('Current user:', currentUser)
  console.log('Current user role:', currentUser?.role)
  const isSuperAdmin = currentUser?.role === 'SuperAdmin' || currentUser?.role === 'super_admin'
  
  // Show access denied if not SuperAdmin
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You need SuperAdmin privileges to access this page.</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-4 px-3 sm:px-0 max-w-full overflow-hidden">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-3 py-2 rounded-md shadow-lg flex items-center space-x-2 animate-slide-in">
          <UserCheck className="w-4 h-4" />
          <span className="text-sm">{successMessage}</span>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-md p-2 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-300">{error}</p>
            <button 
              onClick={() => dispatch(clearError())}
              className="text-xs text-red-400 underline mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Admin Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage administrative users and their permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="h-9 px-3 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Admin</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full pl-10 pr-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="h-9 px-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="support_admin">Support Admin</option>
            <option value="content_admin">Content Admin</option>
            <option value="finance_admin">Finance Admin</option>
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="h-9 px-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          {/* Refresh Button */}
          <button 
            onClick={handleRefresh}
            disabled={loading}
            className="h-9 px-3 text-sm bg-background border border-border rounded-md hover:bg-muted transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          
          {/* Export Button */}
          <button className="h-9 px-3 text-sm bg-background border border-border rounded-md hover:bg-muted transition-colors flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-blue-500/10 rounded-md">
              <Users className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Admins</p>
              <p className="text-lg font-semibold text-foreground">{admins.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-green-500/10 rounded-md">
              <UserCheck className="w-4 h-4 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-lg font-semibold text-foreground">
                {admins.filter((a) => a.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-gray-500/10 rounded-md">
              <UserX className="w-4 h-4 text-gray-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Inactive</p>
              <p className="text-lg font-semibold text-foreground">
                {admins.filter((a) => a.status === 'inactive').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-red-500/10 rounded-md">
              <Shield className="w-4 h-4 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Locked</p>
              <p className="text-lg font-semibold text-foreground">
                {admins.filter((a) => a.status === 'locked').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Table */}
      <div className="bg-card border border-border rounded-md overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Role
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Department
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  2FA
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && filteredAdmins.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                      <p className="text-muted-foreground">Loading admin users...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredAdmins.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                    No admin users found
                  </td>
                </tr>
              ) : (
                filteredAdmins.map((admin) => (
                <tr key={admin.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {admin.firstName[0]}{admin.lastName[0]}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-foreground">
                          {admin.firstName} {admin.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(admin.role)}`}>
                      {admin.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(admin.status)}`}>
                      {admin.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {admin.department || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {admin.lastLogin ? (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDistanceToNow(admin.lastLogin, { addSuffix: true })}</span>
                      </div>
                    ) : (
                      'Never'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {admin.twoFactorEnabled ? (
                      <Shield className="w-4 h-4 text-green-500" />
                    ) : (
                      <Shield className="w-4 h-4 text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="relative">
                      <button
                        onClick={() => setShowContextMenu(showContextMenu === admin.id ? null : admin.id)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-muted-foreground" />
                      </button>

                      {showContextMenu === admin.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => handleViewDetails(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View Details</span>
                          </button>
                          <button
                            onClick={() => handleViewAuditLogs(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                          >
                            <Clock className="w-4 h-4" />
                            <span>View Audit Logs</span>
                          </button>
                          <button
                            onClick={() => handleEditAdmin(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleResetPassword(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                          >
                            <Key className="w-4 h-4" />
                            <span>Reset Password</span>
                          </button>
                          <button
                            onClick={() => handleToggleStatus(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                          >
                            {admin.status === 'active' ? (
                              <>
                                <UserX className="w-4 h-4" />
                                <span>Deactivate</span>
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4" />
                                <span>Activate</span>
                              </>
                            )}
                          </button>
                          <hr className="my-1 border-border" />
                          <button
                            onClick={() => handleDeleteAdmin(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-500/10 text-red-500 transition-colors flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Mobile Card View */}
        <div className="lg:hidden">
          {loading && filteredAdmins.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading admin users...</p>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No admin users found
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredAdmins.map((admin) => (
                <div key={admin.id} className="p-4 hover:bg-muted/30 transition-colors">
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-white">
                          {admin.firstName?.charAt(0) || admin.email?.charAt(0) || 'A'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {admin.firstName} {admin.lastName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{admin.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(admin.status)}`}>
                      {admin.status}
                    </span>
                  </div>
                  
                  {/* Card Body */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">Role</span>
                      <p className="text-sm font-medium mt-1">{admin.role}</p>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">Department</span>
                      <p className="text-sm mt-1">{admin.department || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">2FA</span>
                      <div className="flex items-center mt-1">
                        {admin.twoFactorEnabled ? (
                          <Shield className="w-4 h-4 text-green-500" />
                        ) : (
                          <Shield className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="ml-1 text-xs">{admin.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">Last Login</span>
                      <p className="text-xs mt-1">
                        {admin.lastLoginAt 
                          ? formatDistanceToNow(new Date(admin.lastLoginAt), { addSuffix: true })
                          : 'Never'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Card Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <button
                      onClick={() => handleViewDetails(admin)}
                      className="text-sm text-primary hover:underline"
                    >
                      View Details
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowContextMenu(showContextMenu === admin.id ? null : admin.id)
                      }}
                      className="p-1 hover:bg-muted rounded relative"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Context Menu */}
                  {showContextMenu === admin.id && (
                    <div className="absolute right-4 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-10">
                      <button
                        onClick={() => handleViewDetails(admin)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2"
                      >
                        <Eye className="w-4 h-4" />
                        <span>View Details</span>
                      </button>
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={() => handleEditAdmin(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2"
                          >
                            <UserCog className="w-4 h-4" />
                            <span>Edit Admin</span>
                          </button>
                          <button
                            onClick={() => handleResetPassword(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2"
                          >
                            <Key className="w-4 h-4" />
                            <span>Reset Password</span>
                          </button>
                          <button
                            onClick={() => handleToggleStatus(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2"
                          >
                            {admin.status === 'active' ? (
                              <>
                                <UserX className="w-4 h-4" />
                                <span>Suspend Admin</span>
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4" />
                                <span>Activate Admin</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteAdmin(admin)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center space-x-2 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete Admin</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <AdminModal
          mode="create"
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateAdmin}
        />
      )}

      {showEditModal && selectedAdmin && (
        <AdminModal
          mode="edit"
          admin={selectedAdmin}
          onClose={() => {
            setShowEditModal(false)
            setSelectedAdmin(null)
          }}
          onSave={handleUpdateAdmin}
        />
      )}

      {showDetailsModal && selectedAdmin && (
        <AdminDetailsModal
          admin={selectedAdmin}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedAdmin(null)
          }}
        />
      )}

      {showPasswordModal && selectedAdmin && (
        <PasswordResetModal
          admin={selectedAdmin}
          onClose={() => {
            setShowPasswordModal(false)
            setSelectedAdmin(null)
          }}
          onReset={handlePasswordReset}
        />
      )}
      
      {showAuditLogModal && selectedAdmin && (
        <AuditLogModal
          adminId={selectedAdmin.id}
          adminName={`${selectedAdmin.firstName} ${selectedAdmin.lastName}`}
          onClose={() => {
            setShowAuditLogModal(false)
            setSelectedAdmin(null)
          }}
        />
      )}
    </div>
  )
}

export default AdminManagement